
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Download, Mail, Loader2 } from 'lucide-react';
import { generateCustomerReceipt, downloadPDFFromDataURI } from '@/utils/customerReceiptGenerator';
import ReceiptPreview from '@/components/shared/ReceiptPreview';

interface Customer {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  postcode: string | null;
  created_at: string;
  payment_links?: {
    amount: number;
    currency: string;
    description: string;
  };
}

interface PDFPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
  businessProfile: any;
  onEmailReceipt?: (customer: Customer, pdfData: string) => void;
}

const PDFPreviewDialog = ({ 
  open, 
  onOpenChange, 
  customer, 
  businessProfile,
  onEmailReceipt 
}: PDFPreviewDialogProps) => {
  const { toast } = useToast();
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    if (open && customer && businessProfile) {
      generateReceiptPDF();
    } else {
      setPdfData(null);
    }
  }, [open, customer, businessProfile]);

  const generateReceiptPDF = async () => {
    if (!customer) return;
    
    setLoading(true);
    try {
      const pdfDataUri = await generateCustomerReceipt(customer, businessProfile);
      setPdfData(pdfDataUri);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF receipt",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (pdfData && customer) {
      const filename = `receipt-${customer.name || 'customer'}-${customer.id.substring(0, 8)}.pdf`;
      downloadPDFFromDataURI(pdfData, filename);
      
      toast({
        title: "Download Started",
        description: "PDF receipt is being downloaded",
      });
    }
  };

  const handleEmailReceipt = async () => {
    if (!customer || !pdfData || !onEmailReceipt) return;
    
    setEmailLoading(true);
    try {
      await onEmailReceipt(customer, pdfData);
    } catch (error) {
      console.error('Error emailing receipt:', error);
    } finally {
      setEmailLoading(false);
    }
  };

  // Create receipt data for the HTML preview
  const receiptData = customer ? {
    receiptNumber: `REC-${customer.id.substring(0, 8).toUpperCase()}`,
    date: new Date(customer.created_at).toLocaleDateString('en-GB'),
    amount: customer.payment_links?.amount || 0,
    currency: customer.payment_links?.currency || 'GBP',
    description: customer.payment_links?.description,
    customerName: customer.name || undefined,
    customerEmail: customer.email || undefined,
    customerPhone: customer.phone || undefined,
    customerAddress: (customer.address || customer.postcode) ? {
      street: customer.address || undefined,
      postcode: customer.postcode || undefined
    } : undefined,
    businessProfile,
    includeAddress: !!(customer.address || customer.postcode)
  } : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] w-[95vw] p-0 flex flex-col">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle>
            Receipt Preview - {customer?.name || 'Customer'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 h-[calc(95vh-200px)]">
          <div className="px-6">
            {loading ? (
              <div className="flex items-center justify-center h-[400px]">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading receipt preview...</p>
                </div>
              </div>
            ) : receiptData ? (
              <ReceiptPreview receiptData={receiptData} />
            ) : (
              <div className="flex items-center justify-center h-[400px]">
                <p className="text-gray-600">Unable to load receipt preview</p>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 p-6 pt-0 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {pdfData && (
            <>
              <Button variant="outline" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              {customer?.email && onEmailReceipt && (
                <Button 
                  onClick={handleEmailReceipt}
                  disabled={emailLoading}
                >
                  {emailLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Email Receipt
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PDFPreviewDialog;
